import { z } from 'zod';
import { shiftDate } from './calendar.js';
import { LEVELS, LEVEL_ORDER } from './enums.js';
import { rollNoSchema, schoolCodeSchema } from './auth.js';
import { MASTERY } from './rules.js';
/**
 * A parent's read-only view of one child (spec section 3.2).
 *
 * A parent signs in with the school code, the child's roll number and a parent
 * PIN the teacher issues — never the child's own PIN, so a parent signed in
 * cannot answer practice as the child, and the child's PIN can be reset
 * without locking the parent out. What a parent sees is the child's own
 * progress over time: never a class average or a rank, and never what the child
 * asked the tutor.
 */
export const PARENT = {
    /** Two more digits than a student's: a parent's phone stays signed in. */
    pinLength: 6,
    /** A parent's own phone, not a classroom one. */
    sessionDays: 30,
    /** How many weeks the accuracy trend covers. */
    trendWeeks: 8,
    /** How many days the study calendar covers: five whole weeks. */
    calendarDays: 35,
};
export const parentPinSchema = z
    .string()
    .trim()
    .regex(new RegExp(`^\\d{${PARENT.pinLength}}$`), `A parent PIN is ${PARENT.pinLength} digits.`);
export const parentLoginSchema = z.object({
    schoolCode: schoolCodeSchema,
    rollNo: rollNoSchema,
    pin: parentPinSchema,
});
/** How dark a day is on the study calendar. */
export function activityLevel(minutes) {
    if (minutes <= 0)
        return 0;
    if (minutes < 15)
        return 1;
    if (minutes < 40)
        return 2;
    return 3;
}
/**
 * Days in a row with some study, counting back from today. A day not yet
 * studied today does not break it: at breakfast a five-day streak is still
 * five, and becomes six once the child practices.
 */
export function studyStreak(activeDates, today) {
    let day = activeDates.has(today) ? today : shiftDate(today, -1);
    let count = 0;
    while (activeDates.has(day)) {
        count += 1;
        day = shiftDate(day, -1);
    }
    return count;
}
/**
 * Share right, week by week, for `weeks` seven-day windows ending today, oldest
 * first. Rolling windows rather than calendar weeks, so the last point is never
 * a week that has only just begun.
 */
export function weeklyAccuracy(answers, today, weeks = PARENT.trendWeeks) {
    return Array.from({ length: weeks }, (_, index) => {
        const end = shiftDate(today, -7 * (weeks - 1 - index));
        const start = shiftDate(end, -6);
        let correct = 0;
        let total = 0;
        for (const row of answers) {
            if (row.date >= start && row.date <= end) {
                correct += row.correct;
                total += row.total;
            }
        }
        return { weekStart: start, pct: total === 0 ? null : Math.round((correct / total) * 100) };
    });
}
/**
 * Every time the child moved up a level in a chapter, newest first. The first
 * level a chapter is given is where the child starts, not a step up, and a step
 * down is never a milestone: a parent sees the level as it is, and the moves up.
 */
export function movedUpEvents(history) {
    const ordered = [...history].sort((a, b) => a.at.localeCompare(b.at));
    const last = new Map();
    const ups = [];
    for (const row of ordered) {
        const before = last.get(row.topicId);
        if (before !== undefined && LEVEL_ORDER[row.level] > LEVEL_ORDER[before])
            ups.push(row);
        last.set(row.topicId, row.level);
    }
    return ups.reverse();
}
const QUESTION_MARKS = [25, 50, 100, 250, 500, 1000, 2000, 5000];
/** The largest round number of answered questions reached, or null below the first. */
export function questionMilestone(answered) {
    let reached = null;
    for (const mark of QUESTION_MARKS)
        if (answered >= mark)
            reached = mark;
    return reached;
}
/** Whole days from `today` to `examDate`, or null once it has passed or with none. */
export function daysUntil(examDate, today) {
    if (!examDate)
        return null;
    const days = Math.round((Date.parse(`${examDate}T12:00:00Z`) - Date.parse(`${today}T12:00:00Z`)) / 86_400_000);
    return days >= 0 ? days : null;
}
/** The word a parent reads beside a strength score. */
export function strengthWord(strength) {
    if (strength === null)
        return 'Not started';
    if (strength >= MASTERY.strongAtOrAbove)
        return 'Strong';
    if (strength > MASTERY.weakAtOrBelow)
        return 'Getting there';
    return 'Building up';
}
/** How many chapters sit at each level, in level order, zeros included. */
export function levelMix(levels) {
    return LEVELS.map((level) => ({ level, count: levels.filter((row) => row === level).length }));
}
/**
 * Which way the right-answer share is going: the latest week with answers
 * against the mean of the weeks before it. Null with fewer than two weeks of
 * answers, since one week is a number, not a direction.
 */
export function trendDirection(weeks, margin = 5) {
    const known = weeks.map((week) => week.pct).filter((pct) => pct !== null);
    const last = known.at(-1);
    if (last === undefined || known.length < 2)
        return null;
    const before = known.slice(0, -1);
    const mean = before.reduce((sum, pct) => sum + pct, 0) / before.length;
    if (last >= mean + margin)
        return 'up';
    if (last <= mean - margin)
        return 'down';
    return 'steady';
}
//# sourceMappingURL=parent.js.map